import type { ChartData, ChartOptions } from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import React, { useMemo } from "react"
import { Line } from "react-chartjs-2"

import Typography from "~/components/atoms/Typography"
import { Color } from "~/consts/style-mixins"
import { CuratedNewGradJobDetail } from "~/entities/CuratedNewGradJob"
import { NewGradJobDetail } from "~/entities/NewGradJob"
import { useMobileMedia } from "~/hooks/useMobileMedia"

import { CareerPathCard } from "../_Rich/_SeniorCareerPath"
import { CareerPathCardStack } from "../_Rich/_SeniorCareerPath/styled"

import * as styled from "./styled"

type Salary = NewGradJobDetail["salaries"][number]

const OPTION: ChartOptions = {
  scales: {
    y: {
      max: 1000,
      ticks: {
        stepSize: 200,
      },
    },
  },
  plugins: { legend: { display: false } },
}

const useChartData = (job: Props["job"]) => {
  const isMobileMedia = useMobileMedia()

  return useMemo((): ChartData | undefined => {
    if (!("salaries" in job) || job.salaries.length === 0) {
      return
    }
    const salariesWithEmpty: Array<Partial<Salary>> = [{}, ...job.salaries]
    const labels: ChartData["labels"] = salariesWithEmpty.map(({ year }) => (year != null ? `${year}年目` : ""))
    const datasets: ChartData["datasets"] = [
      {
        data: salariesWithEmpty.map(({ amount }) => amount ?? 0),
        pointBackgroundColor: Color.Primary,
        pointRadius: isMobileMedia ? 4 : 8,
        borderColor: Color.Primary,
        label: "年収",
        // ↓ plugin chartjs-plugin-datalabels
        datalabels: {
          color: Color.Primary,
          formatter: (value: number) => (value === 0 ? "" : `${value}万`),
          font: {
            weight: "bold",
            size: 14,
          },
          align: "top", // 文字をグラフのピンからどこに出すか
          offset: 10, // ピンからの距離
        },
      },
    ]

    return { labels, datasets }
  }, [job, isMobileMedia])
}

interface Props {
  job: NewGradJobDetail | CuratedNewGradJobDetail
}

const CareerPath = ({ job }: Props) => {
  const chartData = useChartData(job)

  return (
    <styled.EachTabContainer>
      <div className="content-wrapper">
        <div className="each-section">
          <Typography type="Heading" className="each-section-heading">
            {job.company.name}でのキャリアパス
          </Typography>
          {job.mission && (
            <div className="section-content">
              <p className="section-detail-content">{job.career_path}</p>
            </div>
          )}
        </div>

        {"careers" in job && job.careers.length !== 0 && (
          <div className="each-section">
            <Typography type="Heading" className="each-section-heading">
              同じ職種で働く先輩のキャリアパス
            </Typography>
            <CareerPathCardStack>
              {job.careers.map((career) => (
                <CareerPathCard career={career} />
              ))}
            </CareerPathCardStack>
          </div>
        )}

        {chartData && (
          <div className="each-section">
            <Typography type="Heading" className="each-section-heading">
              給与推移
            </Typography>
            <Line data={chartData} options={OPTION} plugins={[ChartDataLabels]} />
          </div>
        )}
      </div>
    </styled.EachTabContainer>
  )
}

export default CareerPath
